import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerStatusPills from "../../shared/ServerStatusPills";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { benefit, Datum } from "../../interface/Benefit";
import { BenefitAxiosInstance } from "../../Network/BenefitAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Option } from "../../interface/Option";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ActivitySelector from "../Shared/ActivityType";
import ListOfDrugCode from "../Shared/DrugCode";
import ListOfDrugMedical from "../Shared/DrugMedicalGroup";
import ListOfDrugCustom from "../Shared/DrugCustomGroup";
import PolicyNames from "../Shared/PolicyNames";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import ListOfProviderName from "../Shared/ListOfProviderName";
import ListOfDoctorName from "../Shared/Doctor";
import FileUpload from "Pages/uploadFile/Benefit/Benefit";
import Vip from "../Shared/Vip";
import Patient from "../Shared/Patient";
import { ScrollArea } from "../../components/ui/scroll-area";

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ icon: string; bg: string; border: string; color: string; title: string; required?: boolean }> = ({ icon, bg, border, color, title, required }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
    <div style={{ width: 26, height: 26, borderRadius: 7, background: bg, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${border}` }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: 11, color }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</span>
    {required && <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>*required</span>}
  </div>
);
const AccordionSection: React.FC<{
  id: string; icon: string; bg: string; border: string; color: string;
  title: string; required?: boolean; badge?: number; hasError?: boolean;
  isOpen: boolean; onToggle: () => void; children: React.ReactNode;
}> = ({ icon, bg, border, color, title, required, badge, hasError, isOpen, onToggle, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${hasError ? "#fca5a5" : "#e2e8f0"}`, boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 12, overflow: "hidden" }}>
    <button
      type="button"
      onClick={onToggle}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: isOpen ? "#f8fafc" : "#fff", border: "none", cursor: "pointer", textAlign: "left" }}
    >
      <div style={{ width: 28, height: 28, borderRadius: 8, background: bg, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${border}`, flexShrink: 0 }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize: 12, color }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</span>
      {required && <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>*required</span>}
      {hasError && <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 12, color: "#dc2626" }} />}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
        {typeof badge === "number" && badge > 0 && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, border: "1px solid #bfdbfe" }}>{badge} selected</span>
        )}
        <i className={`fa-solid fa-chevron-down`} style={{ fontSize: 11, color: "#94a3b8", transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.15s" }} />
      </div>
    </button>
    {isOpen && <div style={{ padding: "4px 16px 16px" }}>{children}</div>}
  </div>
);
const ErrorBanner: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
    <i className="fa-solid fa-circle-exclamation" style={{ color: "#dc2626", marginTop: 2, fontSize: 12 }} />
    <p style={{ fontSize: 12, color: "#991b1b", margin: 0 }}>{text}</p>
  </div>
);

const ToggleChip: React.FC<{ active: boolean; onClick: () => void; activeColor: string; activeBg: string; label: string; dot?: boolean; small?: boolean }> = ({ active, onClick, activeColor, activeBg, label, dot, small }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
      flex: small ? "none" : 1, padding: small ? "4px 10px" : "8px 0",
      borderRadius: 8, fontSize: small ? 11 : 12, fontWeight: 600, cursor: "pointer",
      border: active ? `1px solid ${activeColor}` : "1px solid #e2e8f0",
      background: active ? activeBg : "#fff",
      color: active ? activeColor : "#64748b",
      transition: "all 0.15s",
    }}
  >
    {dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />}
    {label}
  </button>
);

// ─── Helper ───────────────────────────────────────────────────────────────────
const TagsWrap: React.FC<{ items: string[]; color: "blue" | "green" | "purple" | "orange" | "gray" }> = ({ items, color }) => {
  const styles: Record<string, React.CSSProperties> = {
    blue:   { background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" },
    green:  { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" },
    purple: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" },
    orange: { background: "#fff7ed", color: "#c2410c", border: "0.5px solid #fed7aa" },
    gray:   { background: "#f8fafc", color: "#475569", border: "0.5px solid #e2e8f0" },
  };
  if (!items || !items.length)
    return <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {items.map((item, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...styles[color] }}>
          {item}
        </span>
      ))}
    </div>
  );
};

// Reusable ICD sub-section card
const IcdCard: React.FC<{ dot: string; label: string; items: string[]; color: "blue" | "green" | "purple" | "orange" | "gray" }> = ({ dot, label, items, color }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 10 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
      <span style={{ width: 6, height: 6, borderRadius: 2, background: dot, flexShrink: 0 }} />
<span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>
  {label}
</span>
      <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto", fontWeight: 500 }}>{items.length} items</span>
    </div>
    <TagsWrap items={items} color={color} />
  </div>
);



// ─── Form UX Helpers ──────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const FieldPanel: React.FC<{ title: string; subtitle?: string; tone?: "blue" | "purple" | "orange" | "gray"; children: React.ReactNode }> = ({ title, subtitle, tone = "gray", children }) => {
  const styles: Record<string, string> = {
    blue: "border-blue-200 bg-blue-50/50",
    purple: "border-purple-200 bg-purple-50/50",
    orange: "border-orange-200 bg-orange-50/50",
    gray: "border-gray-200 bg-gray-50/60",
  };
  return (
    <div className={`rounded-xl border p-4 ${styles[tone]}`}>
      <div className="mb-3">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
};

const ScopeCard: React.FC<{
  label: string;
  icon: string;
  color: "blue" | "cyan" | "purple" | "amber" | "emerald" | "pink" | "yellow" | "indigo" | "slate";
  isExclude: boolean;
  onModeChange: (exclude: boolean) => void;
  children: React.ReactNode;
}> = ({ label, icon, color, isExclude, onModeChange, children }) => {
  const colors: Record<string, { badge: string; border: string }> = {
    blue: { badge: "bg-blue-50 text-blue-700 border-blue-200", border: "border-blue-100" },
    cyan: { badge: "bg-cyan-50 text-cyan-700 border-cyan-200", border: "border-cyan-100" },
    purple: { badge: "bg-purple-50 text-purple-700 border-purple-200", border: "border-purple-100" },
    amber: { badge: "bg-amber-50 text-amber-700 border-amber-200", border: "border-amber-100" },
    emerald: { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", border: "border-emerald-100" },
    pink: { badge: "bg-pink-50 text-pink-700 border-pink-200", border: "border-pink-100" },
    yellow: { badge: "bg-yellow-50 text-yellow-700 border-yellow-200", border: "border-yellow-100" },
    indigo: { badge: "bg-indigo-50 text-indigo-700 border-indigo-200", border: "border-indigo-100" },
    slate: { badge: "bg-slate-50 text-slate-700 border-slate-200", border: "border-slate-100" },
  };
  const c = colors[color] ?? colors.slate;

  return (
    <div className={`bg-white rounded-xl border ${c.border} p-4 shadow-sm`}>
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${c.badge}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={() => onModeChange(true)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isExclude ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclude
          </button>
          <button
            type="button"
            onClick={() => onModeChange(false)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${!isExclude ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Include
          </button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
        {children}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Benefit: React.FC = () => {
  const [items, setItems] = useState<benefit>({ result: [] });

  const [Name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [PrimaryDiagnosis, setPrimaryDiagnosis] = useState<Option[]>([]);
  const [relatedIcds, setRelatedIcds] = useState<Option[]>([]);
  const [relatedActivity, setRelatedActivity] = useState<Option[]>([]);
  const [relatedPrimaryMedicalGroups, setRelatedPrimaryMedicalGroups] = useState<Option[]>([]);
  const [relatedPrimaryCustomGroups, setRelatedPrimaryCustomGroups] = useState<Option[]>([]);
  const [relatedIcdMedicalGroups, setRelatedIcdMedicalGroups] = useState<Option[]>([]);
  const [relatedIcdCustomGroups, setRelatedIcdCustomGroups] = useState<Option[]>([]);
  const [relatedActivitiesCustomGroups, setRelatedActivitiesCustomGroups] = useState<Option[]>([]);
  const [relatedActivitiesMedicalGroups, setRelatedActivitiesMedicalGroups] = useState<Option[]>([]);
  const [mainDrug, setMainDrug] = useState<Option[]>([]);
  const [mainDrugMedicalGroups, setMainDrugMedicalGroups] = useState<Option[]>([]);
  const [mainDrugCustomGroups, setMainDrugCustomGroups] = useState<Option[]>([]);
  const [ActivityType, setActivityType] = useState<string>("");
  const [IcdDescription, setIcdDescription] = useState<string>("");
  const [maxCoverageOp, setMaxCoverageOp] = useState<number>(0);
  const [isOp, setisOp] = useState<boolean>(false);
  const [maxCoverageIp, setMaxCoverageIp] = useState<number>(0);
  const [isIp, setisIp] = useState<boolean>(false);
  const [IsActive, setIsActive] = useState<boolean>(true);
  const [copay, setCopay] = useState<number>(0);
  const [maxCopay, setMaxCopay] = useState<number>(0);
  const [provider, setProvider] = useState<Option[]>([]);
  const [applyToOtherProviders, setApplyToOtherProviders] = useState<boolean>(false);
  const [networks, setNetworks] = useState<Option[]>([]);
  const [apply_to_other_networks, setApply_to_other_networks] = useState<boolean>(false);
  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [apply_to_other_insuranceplan, setapply_to_other_insuranceplan] = useState<boolean>(false);
  const [Payer, setPayer] = useState<string[]>([]);
  const [apply_to_other_payer, setapply_to_other_payer] = useState<boolean>(false);
  const [patients, setPatients] = useState<Option[]>([]);
  const [apply_to_other_patients, setApply_to_other_patients] = useState<boolean>(false);
  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [apply_to_other_policy, setApply_to_other_policy] = useState<boolean>(false);
  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [apply_to_other_user, setApply_to_other_user] = useState<boolean>(false);
  const [apply_to_other_vip, setApply_to_other_vip] = useState<boolean>(false);
  const [doctor, setDoctor] = useState<Option[]>([]);
  const [applyToOtherDoctor, setApplyToOtherDoctor] = useState<boolean>(false);
  const [vip, setVip] = useState<Option[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");
  const [allCodesOpen, setAllCodesOpen] = useState(false);
const [allCodesTitle, setAllCodesTitle] = useState("");
const [showAllUpdates, setShowAllUpdates] = useState(false);
const [allCodesList, setAllCodesList] = useState<Array<{label: string; kind: "icd"|"medical"|"custom"}>>([]);
const [openSection, setOpenSection] = useState<string | null>("basic");

  const [clearPrimaryDiagnosis, setClearPrimaryDiagnosis] = useState(false);
  const [clearIcdmedicalcode, setClearIcdmedicalcode] = useState(false);
  const [clearRelatedActivitiesCustomGroups, setClearRelatedActivitiesCustomGroups] = useState(false);
  const [clearRelatedActivitiesMedicalGroups, setClearRelatedActivitiesMedicalGroups] = useState(false);
  const [clearRelatedIcdMedicalGroups, setClearRelatedIcdMedicalGroups] = useState(false);
  const [clearRelatedIcdCustomGroups, setClearRelatedIcdCustomGroups] = useState(false);
  const [clearRelatedPrimaryMedicalGroups, setClearRelatedPrimaryMedicalGroups] = useState(false);
  const [clearRelatedPrimaryCustomGroups, setClearRelatedPrimaryCustomGroups] = useState(false);
  const [clearActivityCode, setClearActivityCode] = useState(false);
  const [activityTypeisValid, setActivityTypeIsValid] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [selectedIcdOption, setSelectedIcdOption] = useState<string | null>(null);
  const [selectedPrimaryIcdOption, setSelectedPrimaryIcdOption] = useState<string | null>(null);
  const [selectedActivityOption, setSelectedActivityOption] = useState<string | null>(null);
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [clearMainDrug, setClearMainDrug] = useState(false);
  const [clearMainDrugMedicalGroups, setClearMainDrugMedicalGroups] = useState(false);
  const [clearMainDrugCustomGroups, setClearMainDrugCustomGroups] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [icdError, setIcdError] = useState("");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // View dialog
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const clear = () => {
    setName(""); setMaxCoverageOp(0); setDescription("");
    setRelatedActivitiesCustomGroups([]); setRelatedIcds([]); setRelatedIcdMedicalGroups([]);
    setRelatedActivitiesMedicalGroups([]); setRelatedPrimaryCustomGroups([]); setRelatedPrimaryMedicalGroups([]);
    setRelatedIcdCustomGroups([]); setRelatedActivity([]); setMainDrug([]);
    setMainDrugMedicalGroups([]); setMainDrugCustomGroups([]); setisOp(false);
    setMaxCoverageIp(0); setisIp(false); setCopay(0); setMaxCopay(0); setIsActive(true);
    setSelectedActivityOption(null); setSelectedIcdOption(null); setSelectedMainOption(null);
    setProvider([]); setApplyToOtherProviders(false); setNetworks([]);
    setApply_to_other_networks(false); setInsuranceplan([]);
    setapply_to_other_insuranceplan(false); setPayer([]);
    setapply_to_other_payer(false); setPatients([]);
    setApply_to_other_patients(false); setApply_to_other_vip(false);
    setApply_to_other_policy(false); setApply_to_other_user(false);
    setPolicy([]); setUserLevel([]); setPrimaryDiagnosis([]);
    setActivityType(""); setVip([]); setDoctor([]); setApplyToOtherDoctor(false);
    setSelectedPrimaryIcdOption(null);
  };

  const handleOpenDialog = (item: Datum) => { setSelectedItem(item); setIsViewDialogOpen(true); };
const handleCloseDialog = () => { setIsViewDialogOpen(false); setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Datum) => { handleEdit(item); setIsDialogOpen(true); };

  const handleMainDrugChange = (selectedOptions: Option[]) => setMainDrug(selectedOptions);
  const handleInsuranceplanChange = (selectedOptions: Option[]) => setInsuranceplan(selectedOptions);
  const handleNetworkChange = (selectedOptions: Option[]) => setNetworks(selectedOptions);
  const handleMainDrugMedicalGroupsChange = (selectedOptions: Option[]) => setMainDrugMedicalGroups(selectedOptions);
  const handleMainDrugCustomGroupsChange = (selectedOptions: Option[]) => setMainDrugCustomGroups(selectedOptions);
  const handleRelatedIcdsChange = (selectedOptions: Option[]) => setRelatedIcds(selectedOptions);
  const handlePrimaryDiagnosisChange = (selectedOptions: Option[]) => setPrimaryDiagnosis(selectedOptions);
  const handleRelatedActivityChange = (selectedOptions: Option[]) => setRelatedActivity(selectedOptions);
  const handleRelatedActivitiesCustomGroupsChange = (selectedOptions: Option[]) => setRelatedActivitiesCustomGroups(selectedOptions);
  const handleRelatedIcdCustomGroupsChange = (selectedOptions: Option[]) => setRelatedIcdCustomGroups(selectedOptions);
  const handleRelatedActivitiesMedicalGroupsChange = (selectedOptions: Option[]) => setRelatedActivitiesMedicalGroups(selectedOptions);
  const handleRelatedIcdMedicalGroupsChange = (selectedOptions: Option[]) => setRelatedIcdMedicalGroups(selectedOptions);
  const handleActivityTypeChange = (ActivityType: string) => setActivityType(ActivityType);
  const handleDoctorChange = (selectedOptions: Option[]) => setDoctor(selectedOptions);
  const handleRelatedPrimaryCustomGroupsChange = (selectedOptions: Option[]) => setRelatedPrimaryCustomGroups(selectedOptions);
  const handleRelatedPrimaryMedicalGroupsChange = (selectedOptions: Option[]) => setRelatedPrimaryMedicalGroups(selectedOptions);
  const handlePoliciesChange = (selectedOptions: PolicyItem[]) => setPolicy(selectedOptions);
  const handlePatientChange = (selectedOptions: Option[]) => setPatients(selectedOptions);
  const handleProviderChange = (selectedOptions: Option[]) => setProvider(selectedOptions);
  const handleVipChange = (selectedOptions: Option[]) => setVip(selectedOptions);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, {
    name: string;
    active: ServerStatusFilterValue;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { name: filterName, active: filterStatus, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      BenefitAxiosInstance.get("getBenefit", {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Benefit", variant: "destructive" });
    },
  });

  const toggleActiveStatus = async (item: Datum) => {
    try {
      const ValuesProvider = (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" }));
      const policyData = (item.policy || []).map((p: any) => ({ id: p.id || 0, Policy_id: p.Policy_id || "", Policy_name: p.Policy_name || "", plans: p.plans || [] }));
      const updatedItem = {
        data: {
          name: item.name, description: item.description,
          activity_type: (item as any).activity_type || "",
          primary_diagnosis: item.primary_diagnosis || [],
          primary_diagnosis_medical_groups: (item.primary_diagnosis_medical_groups || []).map((g: any) => g.id ?? g),
          primary_diagnosis_custom_groups: (item.primary_diagnosis_custom_groups || []).map((g: any) => g.id ?? g),
          related_icds: item.related_icds || [],
          related_icd_medical_groups: (item.related_icd_medical_groups || []).map((g: any) => g.id ?? g),
          related_icd_custom_groups: (item.related_icd_custom_groups || []).map((g: any) => g.id ?? g),
          related_activities: item.related_activities || [],
          related_activities_medical_groups: (item.related_activities_medical_groups || []).map((g: any) => g.id ?? g),
          related_activities_custom_groups: (item.related_activities_custom_groups || []).map((g: any) => g.id ?? g),
          related_drugs: item.related_drugs || [],
          related_drugs_medical_groups: (item.related_drugs_medical_groups || []).map((g: any) => g.id ?? g),
          related_drugs_custom_groups: (item.related_drugs_custom_groups || []).map((g: any) => g.id ?? g),
          max_coverage_op: item.max_coverage_op, is_op: item.is_op,
          max_coverage_ip: item.max_coverage_ip, is_ip: item.is_ip,
          copay: item.copay, max_copay: item.max_copay,
          provider: ValuesProvider, apply_to_other_providers: item.apply_to_other_providers,
          networks: (item.networks || []).map((n: any) => n.id ?? n), apply_to_other_networks: item.apply_to_other_networks,
          insuranceplan: (item.insuranceplan || []).map((i: any) => i.id ?? i), apply_to_other_insuranceplan: item.apply_to_other_insuranceplan,
          payer: item.payer || [], apply_to_other_payer: item.apply_to_other_payer,
          patients: (item.patients || []).map((p: any) => p.name ?? p), apply_to_other_patients: item.apply_to_other_patients,
          policy: policyData, apply_to_other_policy: item.apply_to_other_policy,
          user_level: item.user_level || [], apply_to_other_user: item.apply_to_other_user,
          vip: (item.vip || []).map((v: any) => v.id ?? v), apply_to_vip: item.apply_to_vip,
          doctor: (item.doctor || []).map((d: any) => d.Doctor_id ?? d), apply_to_other_doctor: item.apply_to_other_doctor,
          is_active: !item.is_active, id: item.id,
        },
        token,
      };
      const response = await BenefitAxiosInstance.put(`updateBenefit`, updatedItem);
if (response.data.success) {
  setItems((prev) => ({
    result: prev.result.map((i) =>
      i.id === item.id ? { ...i, is_active: !item.is_active } : i
    ),
  }));
  toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
}
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const buildPolicyData = (p: any) => {
    if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
    if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
    if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
    return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
  };

  const buildProviderData = (o: any) => {
    if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
    const match = o.label?.match(/^\((.+)\) (.+)$/);
    if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
    return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
  };

  const addItem = async (): Promise<void> => {
    setActivityError(""); setIcdError("");
    const hasActivityCode = relatedActivity.length > 0 || relatedActivitiesCustomGroups.length > 0 || relatedActivitiesMedicalGroups.length > 0;
    if (!hasActivityCode) { setActivityError("Please select at least one Activity Code option"); toast({ title: "Validation Error", description: "Activity Code is required", variant: "destructive" }); return; }
    const hasIcdCode = PrimaryDiagnosis.length > 0 || relatedIcds.length > 0 || relatedIcdMedicalGroups.length > 0 || relatedIcdCustomGroups.length > 0 || relatedPrimaryCustomGroups.length > 0 || relatedPrimaryMedicalGroups.length > 0;
    if (!hasIcdCode) { setIcdError("Please select at least one ICD Code option"); toast({ title: "Validation Error", description: "ICD Code is required", variant: "destructive" }); return; }
    try {
      const newGroupArray = [{
        name: Name, description: description, activity_type: ActivityType,
        primary_diagnosis: PrimaryDiagnosis.map((o) => o.value),
        primary_diagnosis_medical_groups: relatedPrimaryMedicalGroups.map((o) => o.value),
        primary_diagnosis_custom_groups: relatedPrimaryCustomGroups.map((o) => o.value),
        related_icds: relatedIcds.map((o) => o.value),
        related_icd_medical_groups: relatedIcdMedicalGroups.map((o) => o.value),
        related_icd_custom_groups: relatedIcdCustomGroups.map((o) => o.value),
        related_activities: relatedActivity.map((o) => o.value),
        related_activities_medical_groups: relatedActivitiesMedicalGroups.map((o) => o.value),
        related_activities_custom_groups: relatedActivitiesCustomGroups.map((o) => o.value),
        related_drugs: mainDrug.map((o) => o.value),
        related_drugs_medical_groups: mainDrugMedicalGroups.map((o) => o.value),
        related_drugs_custom_groups: mainDrugCustomGroups.map((o) => o.value),
        max_coverage_op: maxCoverageOp, is_op: isOp, max_coverage_ip: maxCoverageIp, is_ip: isIp,
        is_active: IsActive, copay: copay, max_copay: maxCopay,
        provider: provider.map(buildProviderData), apply_to_other_providers: applyToOtherProviders,
        networks: networks.map((o) => o.value), apply_to_other_networks,
        insuranceplan: insuranceplan.map((o) => o.value), apply_to_other_insuranceplan,
        payer: Payer, apply_to_other_payer, patients: patients.map((o) => o.label), apply_to_other_patients,
        policy: policy.map(buildPolicyData), apply_to_other_policy,
        user_level: userLevel, apply_to_other_user,
        vip: vip.map((o) => o.value), apply_to_vip: apply_to_other_vip,
        doctor: doctor.map((o) => o.value), apply_to_other_doctor: applyToOtherDoctor,
      }];
      const response = await BenefitAxiosInstance.post("addNewBenefit", { data: newGroupArray, token });
     if (response.data.success) { await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);  toast({ title: "Success", description: "New Benefit added successfully." }); }
      else toast({ title: "Error", description: response.data.message || "Failed to add.", variant: "destructive" });
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    setName(item.name); setDescription(item.description);
    const mapToOptions = (arr: (string | number)[]): Option[] => arr.map((value) => ({ value, label: value.toString() }));
    setRelatedIcds(mapToOptions(item.related_icds || []));
    setPrimaryDiagnosis(mapToOptions(item.primary_diagnosis || []));
    setRelatedActivity(mapToOptions(item.related_activities || []));
    setMainDrug(mapToOptions(item.related_drugs || []));
    const mapGroups = (arr: any[]) => arr.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj);
    setMainDrugCustomGroups(mapGroups(item.related_drugs_custom_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setMainDrugMedicalGroups(mapGroups(item.related_drugs_medical_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedIcdCustomGroups(mapGroups(item.related_icd_custom_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedIcdMedicalGroups(mapGroups(item.related_icd_medical_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedPrimaryCustomGroups(mapGroups(item.primary_diagnosis_custom_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedPrimaryMedicalGroups(mapGroups(item.primary_diagnosis_medical_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedActivitiesCustomGroups(mapGroups(item.related_activities_custom_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setRelatedActivitiesMedicalGroups(mapGroups(item.related_activities_medical_groups || []).map((o) => ({ value: o.id, label: o.name })));
    setMaxCoverageIp(item.max_coverage_ip); setMaxCoverageOp(item.max_coverage_op);
    setisIp(item.is_ip); setisOp(item.is_op); setCopay(item.copay); setIsActive(item.is_active); setMaxCopay(item.max_copay);
    const networksData = (item.networks || []).map((obj: any) => typeof obj === "string" ? { id: 0, Network_name: obj } : obj);
    setNetworks(networksData.map((obj: any) => ({ value: obj.id, label: obj.Network_name || obj.network_name || "" })));
    const vipData = (item.vip || []).map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj);
    setVip(vipData.map((obj: any) => ({ value: obj.id, label: obj.name })));
    const insuranceplanData = (item.insuranceplan || []).map((obj: any) => typeof obj === "string" ? { id: 0, Plan_name: obj } : obj);
    setInsuranceplan(insuranceplanData.map((obj: any) => ({ value: obj.id, label: obj.Plan_name || obj.name || "" })));
    const ProvidersData = (item.provider || []).map((obj: any) => typeof obj === "string" ? { Provider_id: 0, Provider_name: obj } : obj);
    setProvider(ProvidersData.map((obj: any) => ({ value: obj.Provider_id, label: obj.Provider_name })));
    const PatientsData = (item.patients || []).map((obj: any) => typeof obj === "string" ? { National_id: 0, name: obj } : obj);
    setPatients(PatientsData.map((obj: any) => ({ value: obj.National_id && obj.National_id !== 0 ? obj.National_id : obj.id || obj.name, label: obj.name })));
    setPolicy(Array.isArray(item.policy) ? item.policy.map((obj) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] })) : []);
    const DoctorsData = (item.doctor || []).map((obj: any) => typeof obj === "string" ? { Doctor_id: 0, Doctor_name: obj } : obj);
    setDoctor(DoctorsData.map((obj: any) => ({ value: obj.Doctor_id, label: obj.Doctor_name })));
    setApplyToOtherDoctor(item.apply_to_other_doctor); setApply_to_other_policy(item.apply_to_other_policy);
    setApply_to_other_user(item.apply_to_other_user); setApply_to_other_vip(item.apply_to_vip);
    setUserLevel(item.user_level || []); setApplyToOtherProviders(item.apply_to_other_providers);
    setApply_to_other_networks(item.apply_to_other_networks); setapply_to_other_insuranceplan(item.apply_to_other_insuranceplan);
    setPayer(item.payer || []); setapply_to_other_payer(item.apply_to_other_payer);
    setApply_to_other_patients(item.apply_to_other_patients); setEditIndex(item.id);
    if (item.related_icds?.length > 0) setSelectedIcdOption("list_of_icd");
    else if (item.related_icd_medical_groups?.length > 0) setSelectedIcdOption("medical_icd_groups");
    else if (item.related_icd_custom_groups?.length > 0) setSelectedIcdOption("custom_icd_groups");
    else setSelectedIcdOption(null);
    if (item.primary_diagnosis?.length > 0) setSelectedPrimaryIcdOption("list_of_primary_icd");
    else if (item.primary_diagnosis_medical_groups?.length > 0) setSelectedPrimaryIcdOption("medical_primary_icd_groups");
    else if (item.primary_diagnosis_custom_groups?.length > 0) setSelectedPrimaryIcdOption("custom_primary_icd_groups");
    else setSelectedPrimaryIcdOption(null);
    if (item.related_drugs?.length > 0) setSelectedMainOption("list_of_Drug_code");
    else if (item.related_drugs_medical_groups?.length > 0) setSelectedMainOption("medical_Drug_groups");
    else if (item.related_drugs_custom_groups?.length > 0) setSelectedMainOption("custom_Drug_groups");
    else setSelectedMainOption(null);
    if (item.related_activities?.length > 0) { setSelectedActivityOption("list_of_Activity_code"); setActivityType((item as any).activity_type || "CPT"); }
    else if (item.related_activities_medical_groups?.length > 0) { setSelectedActivityOption("medical_Activity_groups"); setActivityType(""); }
    else if (item.related_activities_custom_groups?.length > 0) { setSelectedActivityOption("custom_Activity_groups"); setActivityType(""); }
    else { setSelectedActivityOption(null); setActivityType(""); }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    setActivityError(""); setIcdError("");
    const hasActivityCode = relatedActivity.length > 0 || relatedActivitiesCustomGroups.length > 0 || relatedActivitiesMedicalGroups.length > 0;
    if (!hasActivityCode) { setActivityError("Please select at least one Activity Code option"); toast({ title: "Validation Error", description: "Activity Code is required", variant: "destructive" }); return; }
    const hasIcdCode = PrimaryDiagnosis.length > 0 || relatedIcds.length > 0 || relatedIcdMedicalGroups.length > 0 || relatedIcdCustomGroups.length > 0 || relatedPrimaryCustomGroups.length > 0 || relatedPrimaryMedicalGroups.length > 0;
    if (!hasIcdCode) { setIcdError("Please select at least one ICD Code option"); toast({ title: "Validation Error", description: "ICD Code is required", variant: "destructive" }); return; }
    try {
      const updatedItem = {
        data: {
          name: Name, description: description, activity_type: ActivityType,
          primary_diagnosis: PrimaryDiagnosis.map((o) => o.value),
          related_icds: relatedIcds.map((o) => o.value),
          related_icd_medical_groups: relatedIcdMedicalGroups.map((o) => o.value),
          related_icd_custom_groups: relatedIcdCustomGroups.map((o) => o.value),
          primary_diagnosis_medical_groups: relatedPrimaryMedicalGroups.map((o) => o.value),
          primary_diagnosis_custom_groups: relatedPrimaryCustomGroups.map((o) => o.value),
          related_activities: relatedActivity.map((o) => o.value),
          related_activities_medical_groups: relatedActivitiesMedicalGroups.map((o) => o.value),
          related_activities_custom_groups: relatedActivitiesCustomGroups.map((o) => o.value),
          related_drugs: mainDrug.map((o) => o.value),
          related_drugs_medical_groups: mainDrugMedicalGroups.map((o) => o.value),
          related_drugs_custom_groups: mainDrugCustomGroups.map((o) => o.value),
          max_coverage_op: maxCoverageOp, is_op: isOp, max_coverage_ip: maxCoverageIp, is_ip: isIp,
          is_active: IsActive, copay: copay, max_copay: maxCopay,
          vip: vip.map((o) => o.value),
          provider: provider.map(buildProviderData), apply_to_other_providers: applyToOtherProviders,
          networks: networks.map((o) => o.value), apply_to_other_networks,
          insuranceplan: insuranceplan.map((o) => o.value), apply_to_other_insuranceplan,
          payer: Payer, apply_to_other_payer, patients: patients.map((o) => o.value), apply_to_other_patients,
          policy: policy.map(buildPolicyData), apply_to_other_policy,
          user_level: userLevel, apply_to_other_user, apply_to_vip: apply_to_other_vip,
          doctor: doctor.map((o) => o.value), apply_to_other_doctor: applyToOtherDoctor, id,
        },
        token,
      };
      const response = await BenefitAxiosInstance.put(`updateBenefit`, updatedItem);
     if (response.data.success) { await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false); toast({ title: "Success", description: "Benefit updated successfully." }); }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Datum) => {
    const rows = ["name;description;primary_diagnosis;primary_diagnosis_medical_groups;primary_diagnosis_custom_groups;related_icds;related_icd_medical_groups;related_icd_custom_groups;related_activities;related_activities_medical_groups;related_activities_custom_groups;related_drugs;related_drugs_medical_groups;related_drugs_custom_groups;max_coverage_op;is_op;max_coverage_ip;is_ip;copay;max_copay;provider;apply_to_other_providers;networks;apply_to_other_networks;insuranceplan;apply_to_other_insuranceplan;payer;apply_to_other_payer;patients;apply_to_other_patients;policy;apply_to_other_policy;user_level;apply_to_other_user;doctor;apply_to_other_doctor;vip;apply_to_vip;is_active"];
    const maxLen = Math.max((item.primary_diagnosis || []).length, (item.primary_diagnosis_medical_groups || []).length, (item.primary_diagnosis_custom_groups || []).length, (item.related_icds || []).length, (item.related_icd_medical_groups || []).length, (item.related_icd_custom_groups || []).length, (item.related_activities || []).length, (item.related_activities_medical_groups || []).length, (item.related_activities_custom_groups || []).length, (item.related_drugs || []).length, (item.related_drugs_medical_groups || []).length, (item.related_drugs_custom_groups || []).length, (item.provider || []).length, (item.networks || []).length, (item.insuranceplan || []).length, (item.patients || []).length, (item.policy || []).length, (item.user_level || []).length, (item.doctor || []).length, (item.payer || []).length, (item.vip || []).length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push([item.name || "", item.description || "", (item.primary_diagnosis || [])[i] || "", ((item.primary_diagnosis_medical_groups || [])[i] as any)?.name || "", ((item.primary_diagnosis_custom_groups || [])[i] as any)?.name || "", (item.related_icds || [])[i] || "", ((item.related_icd_medical_groups || [])[i] as any)?.name || "", ((item.related_icd_custom_groups || [])[i] as any)?.name || "", (item.related_activities || [])[i] || "", ((item.related_activities_medical_groups || [])[i] as any)?.name || "", ((item.related_activities_custom_groups || [])[i] as any)?.name || "", (item.related_drugs || [])[i] || "", ((item.related_drugs_medical_groups || [])[i] as any)?.name || "", ((item.related_drugs_custom_groups || [])[i] as any)?.name || "", item.max_coverage_op || "", item.is_op ? "TRUE" : "FALSE", item.max_coverage_ip || "", item.is_ip ? "TRUE" : "FALSE", item.copay || "", item.max_copay || "", ((item.provider || [])[i] as any)?.Provider_name || "", item.apply_to_other_providers ? "Exclude" : "Include", ((item.networks || [])[i] as any)?.Network_name || ((item.networks || [])[i] as any)?.network_name || "", item.apply_to_other_networks ? "Exclude" : "Include", ((item.insuranceplan || [])[i] as any)?.Plan_name || ((item.insuranceplan || [])[i] as any)?.name || "", item.apply_to_other_insuranceplan ? "Exclude" : "Include", (item.payer || [])[i] || "", item.apply_to_other_payer ? "Exclude" : "Include", ((item.patients || [])[i] as any)?.name || "", item.apply_to_other_patients ? "Exclude" : "Include", ((item.policy || [])[i] as any)?.Policy_name || ((item.policy || [])[i] as any)?.name || "", item.apply_to_other_policy ? "Exclude" : "Include", (item.user_level || [])[i] || "", item.apply_to_other_user ? "Exclude" : "Include", ((item.doctor || [])[i] as any)?.Doctor_name || "", item.apply_to_other_doctor ? "Exclude" : "Include", ((item.vip || [])[i] as any)?.name || "", item.apply_to_vip ? "Exclude" : "Include", item.is_active ? "TRUE" : "FALSE"].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = `Benefit${item.id}.csv`;
    a.click();
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      await BenefitAxiosInstance.delete(`deleteBenefit/${id}`, { data: { token } });
      fetchItems();
      toast({ title: "Success", description: "Benefit deleted successfully." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete Benefit", variant: "destructive" });
    }
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>ID</Button>
      ),
     cell: ({ row }) => (
  <div className="w-full text-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>Name</Button>
      ),
      cell: ({ row }) => <div className="w-full text-center">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>Description</Button>
      ),
      cell: ({ row }) => <div className="w-full text-center">{row.getValue("description")}</div>,
    },
  {
  accessorKey: "primary_diagnosis",
  header: ({ column }) => (
    <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>Primary ICD</Button>
  ),
  cell: ({ row }) => {
    const item = row.original;
    const icdCodes = item.primary_diagnosis ?? [];
    const medical  = item.primary_diagnosis_medical_groups?.map((g: any) => g.name || g) ?? [];
    const custom   = item.primary_diagnosis_custom_groups?.map((g: any) => g.name || g) ?? [];

    const allDisplay = [
      ...icdCodes.map((c: string) => ({ label: c, kind: "icd" as const })),
      ...medical.map((g: string) => ({ label: g, kind: "medical" as const })),
      ...custom.map((g: string)  => ({ label: g, kind: "custom" as const })),
    ];

    const total = allDisplay.length;
    if (total === 0) return <div className="w-full text-center text-gray-400">—</div>;

    const shown = allDisplay.slice(0, 3);
    const kindStyle = (kind: "icd" | "medical" | "custom") => {
      if (kind === "icd")     return { bg: "#011934", color: "#fff",    border: "#011934", tag: "ICD" };
      if (kind === "medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
      return                         { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
    };

    return (
      <div className="w-full px-2 py-1.5">
        <div className="flex flex-wrap justify-center gap-1.5 mb-1">
          {shown.map((c, i) => {
            const s = kindStyle(c.kind);
            return (
              <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: s.bg, border: `1px solid ${s.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{c.label}</span>
                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: s.color, opacity: 0.85, border: `1px solid ${s.border}` }}>{s.tag}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-1">
          <span className="text-xs font-bold text-gray-500">{total}</span>
          {total > 3 && (
            <button
            onClick={() => {
  setAllCodesList(allDisplay);
  setAllCodesTitle("Primary ICD Codes");
  setAllCodesOpen(true);
}}

              className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
            >
              +{total - 3} more →
            </button>
          )}
        </div>
      </div>
    );
  },
},
  {
  accessorKey: "secondary_icds",
  header: ({ column }) => (
    <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>Secondary ICD</Button>
  ),
  cell: ({ row }) => {
    const item = row.original;
    const icdCodes = item.related_icds ?? [];
    const medical  = item.related_icd_medical_groups?.map((g: any) => g.name || g) ?? [];
    const custom   = item.related_icd_custom_groups?.map((g: any) => g.name || g) ?? [];

    const allDisplay = [
      ...icdCodes.map((c: string) => ({ label: c, kind: "icd" as const })),
      ...medical.map((g: string) => ({ label: g, kind: "medical" as const })),
      ...custom.map((g: string)  => ({ label: g, kind: "custom" as const })),
    ];

    const total = allDisplay.length;
    if (total === 0) return <div className="w-full text-center text-gray-400">—</div>;

    const shown = allDisplay.slice(0, 3);
    const kindStyle = (kind: "icd" | "medical" | "custom") => {
      if (kind === "icd")     return { bg: "#011934", color: "#fff",    border: "#011934", tag: "ICD" };
      if (kind === "medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
      return                         { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
    };

    return (
      <div className="w-full px-2 py-1.5">
        <div className="flex flex-wrap justify-center gap-1.5 mb-1">
          {shown.map((c, i) => {
            const s = kindStyle(c.kind);
            return (
              <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: s.bg, border: `1px solid ${s.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{c.label}</span>
                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: s.color, opacity: 0.85, border: `1px solid ${s.border}` }}>{s.tag}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-1">
          <span className="text-xs font-bold text-gray-500">{total}</span>
          {total > 3 && (
            <button
              onClick={() => {
                setAllCodesList(allDisplay);
                setAllCodesTitle("Secondary ICD Codes");
                setAllCodesOpen(true);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
            >
              +{total - 3} more →
            </button>
          )}
        </div>
      </div>
    );
  },
},
    {
      accessorKey: "is_active",
      header: () => <div className="w-full text-center normal-case">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
            <button onClick={() => toggleActiveStatus(item)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center"><Button variant="ghost">View</Button></div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-shield-alt"></i>
          </span>
          Benefits
        </h5>
      </div>
      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={[]}
        searchValue={filterName}
        onSearchChange={setFilterName}
        searchPlaceholder="Search benefits..."
        addButtonLabel="Add Benefit"
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        onEdit={handleEdit}
        onDelete={removeItem}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ServerStatusPills
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(0);
              }}
            />
            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* ===== VIEW DIALOG (new style) ===== */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent
          className="max-w-[960px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}
        >
          {selectedItem && (
            <>
              {/* ── Header ── */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />

                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    {selectedItem.name?.charAt(0).toUpperCase() || "B"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedItem.name}
                      </h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>
                      ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
                      {selectedItem.description && <span style={{ marginLeft: 12, opacity: 0.7 }}>{selectedItem.description}</span>}
                    </p>
                  </div>
                </div>

                {/* Stats cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-heart-pulse", label: "Activities", value: `${(selectedItem.related_activities || []).length + (selectedItem.related_activities_medical_groups || []).length + (selectedItem.related_activities_custom_groups || []).length}`, accent: "#60a5fa" },
                    { icon: "fa-stethoscope", label: "Primary ICD", value: `${(selectedItem.primary_diagnosis || []).length + (selectedItem.primary_diagnosis_medical_groups || []).length + (selectedItem.primary_diagnosis_custom_groups || []).length}`, accent: "#34d399" },
                    { icon: "fa-virus", label: "Secondary ICD", value: `${(selectedItem.related_icds || []).length + (selectedItem.related_icd_medical_groups || []).length + (selectedItem.related_icd_custom_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-pills", label: "Drugs", value: `${(selectedItem.related_drugs || []).length + (selectedItem.related_drugs_medical_groups || []).length + (selectedItem.related_drugs_custom_groups || []).length}`, accent: "#fb923c" },
                  ].map((card, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, textTransform: "capitalize", letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Body ── */}
              <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
                <div style={{ padding: "6px 0 16px" }}>

                  {/* ─ Coverage ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-circle-dollar-to-slot" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Coverage Settings</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                      {[
                        { label: "Max Coverage OP", value: selectedItem.max_coverage_op, icon: "fa-hospital", color: "#3b82f6" },
                        { label: "Is OP", value: selectedItem.is_op ? "✓ Yes" : "✗ No", icon: "fa-toggle-on", color: selectedItem.is_op ? "#10b981" : "#94a3b8" },
                        { label: "Max Coverage IP", value: selectedItem.max_coverage_ip, icon: "fa-bed-pulse", color: "#8b5cf6" },
                        { label: "Is IP", value: selectedItem.is_ip ? "✓ Yes" : "✗ No", icon: "fa-toggle-on", color: selectedItem.is_ip ? "#10b981" : "#94a3b8" },
                        { label: "Copay", value: selectedItem.copay, icon: "fa-percent", color: "#f59e0b" },
                        { label: "Max Copay", value: selectedItem.max_copay, icon: "fa-coins", color: "#ec4899" },
                      ].map((c, i) => (
                        <div key={i} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, background: `${c.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <i className={`fa-solid ${c.icon}`} style={{ fontSize: 10, color: c.color }} />
                            </div>
<span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>
  {c.label}
</span>
                          </div>
                          <span style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{String(c.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Activity ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                        <i className="fa-solid fa-heart-pulse" style={{ fontSize: 12, color: "#3b82f6" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity</span>
                    </div>
                    <IcdCard dot="#3b82f6" label="Activity Codes" items={selectedItem.related_activities || []} color="blue" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <IcdCard dot="#22c55e" label="Medical Activity Groups" items={(selectedItem.related_activities_medical_groups || []).map((g: any) => g.name || g)} color="green" />
                      <IcdCard dot="#a855f7" label="Custom Activity Groups" items={(selectedItem.related_activities_custom_groups || []).map((g: any) => g.name || g)} color="purple" />
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Primary Diagnosis ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-stethoscope" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Primary Diagnosis</span>
                    </div>
                    <IcdCard dot="#22c55e" label="ICD Codes" items={selectedItem.primary_diagnosis || []} color="green" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <IcdCard dot="#3b82f6" label="Medical ICD Groups" items={(selectedItem.primary_diagnosis_medical_groups || []).map((g: any) => g.name || g)} color="blue" />
                      <IcdCard dot="#a855f7" label="Custom ICD Groups" items={(selectedItem.primary_diagnosis_custom_groups || []).map((g: any) => g.name || g)} color="purple" />
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Secondary Diagnosis ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                        <i className="fa-solid fa-virus" style={{ fontSize: 12, color: "#9333ea" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Secondary Diagnosis</span>
                    </div>
                    <IcdCard dot="#a855f7" label="ICD Codes" items={selectedItem.related_icds || []} color="purple" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <IcdCard dot="#22c55e" label="Medical ICD Groups" items={(selectedItem.related_icd_medical_groups || []).map((g: any) => g.name || g)} color="green" />
                      <IcdCard dot="#3b82f6" label="Custom ICD Groups" items={(selectedItem.related_icd_custom_groups || []).map((g: any) => g.name || g)} color="blue" />
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Drugs ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fed7aa" }}>
                        <i className="fa-solid fa-pills" style={{ fontSize: 12, color: "#ea580c" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Drugs</span>
                    </div>
                    <IcdCard dot="#f97316" label="Drug Codes" items={selectedItem.related_drugs || []} color="orange" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <IcdCard dot="#22c55e" label="Medical Drug Groups" items={(selectedItem.related_drugs_medical_groups || []).map((g: any) => g.name || g)} color="green" />
                      <IcdCard dot="#a855f7" label="Custom Drug Groups" items={(selectedItem.related_drugs_custom_groups || []).map((g: any) => g.name || g)} color="purple" />
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Target Scope ─ */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                        <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {[
                        { label: "Providers", icon: "fa-hospital", color: "#3b82f6", items: (selectedItem.provider || []).map((p: any) => p.Provider_name || String(p)), exclude: selectedItem.apply_to_other_providers },
                        { label: "Doctors", icon: "fa-stethoscope", color: "#06b6d4", items: (selectedItem.doctor || []).map((d: any) => d.Doctor_name || String(d)), exclude: selectedItem.apply_to_other_doctor },
                        { label: "Networks", icon: "fa-network-wired", color: "#8b5cf6", items: (selectedItem.networks || []).map((n: any) => n.Network_name || n.network_name || String(n)), exclude: selectedItem.apply_to_other_networks },
                        { label: "Insurance Plans", icon: "fa-shield-halved", color: "#f59e0b", items: (selectedItem.insuranceplan || []).map((i: any) => i.Plan_name || i.name || String(i)), exclude: selectedItem.apply_to_other_insuranceplan },
                        { label: "Policies", icon: "fa-file-lines", color: "#10b981", items: (selectedItem.policy || []).map((p: any) => { const planNames = (Array.isArray(p.plans) ? p.plans : []).map((pl: any) => pl?.Plan_name || pl?.plan_name || pl?.name || "").filter(Boolean).join(", "); return `${p.Policy_id || ""} - ${p.Policy_name || ""}${planNames ? ` (${planNames})` : ""}`; }), exclude: selectedItem.apply_to_other_policy },
                        { label: "Patients", icon: "fa-users", color: "#ec4899", items: (selectedItem.patients || []).map((p: any) => p.name || String(p)), exclude: selectedItem.apply_to_other_patients },
                        { label: "VIP", icon: "fa-star", color: "#eab308", items: (selectedItem.vip || []).map((v: any) => v.name || String(v)), exclude: selectedItem.apply_to_vip },
                        { label: "Payer", icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer || []).map((p: any) => String(p)), exclude: selectedItem.apply_to_other_payer },
                        { label: "User Levels", icon: "fa-user-gear", color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => String(u)), exclude: selectedItem.apply_to_other_user },
                      ].map((e) => (
                        <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                            </div>
                          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, textTransform: "none", background: e.exclude ? "#fef2f2" : "#f0fdf4", color: e.exclude ? "#dc2626" : "#15803d", border: `1px solid ${e.exclude ? "#fecaca" : "#bbf7d0"}` }}>
  {e.exclude ? "Exclusion" : "Inclusion"}
</span>
                          </div>
                          {e.items.length > 0 ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, flex: 1 }}>
                              {e.items.map((item, i) => (
                                <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* ─ Audit Trail ─ */}
                  <div style={{ padding: "20px 28px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {/* Created By */}
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
<span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.05em" }}>Created By</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                        </div>
                      </div>
                      {/* Created On */}
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
<span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.05em" }}>Created On</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).createdat)}</span>
                        </div>
                      </div>
                      {/* Last Updated */}
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
<span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.05em" }}>Last Updated</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).updatedat)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Updated By — only if array has entries */}
           {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.at)}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                  </div>

                </div>
              </ScrollArea>

              {/* ── Footer ── */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ADD / EDIT DIALOG ===== */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[780px] h-[92vh] flex flex-col overflow-hidden p-0 bg-white">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Benefit" : "Edit Benefit"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Fill in the details below to {editIndex === null ? "create" : "update"} a Benefit
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50">
            <SectionCard num={1} title="Basic Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="relation_name" className="text-xs font-semibold text-slate-600">Relation Name</Label>
                  <Input id="relation_name" value={Name} onChange={(e) => setName(e.target.value)} placeholder="Enter Relation Name" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label htmlFor="benefit_description" className="text-xs font-semibold text-slate-600">Description</Label>
                  <Input id="benefit_description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter Description" className="mt-1.5 bg-white" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600 mb-2 block">Status</Label>
                  <div className="grid grid-cols-2 gap-2 max-w-[280px]">
                    <button type="button" onClick={() => setIsActive(true)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${IsActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${!IsActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard num={2} title="Coverage Settings">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldPanel title="Is IP">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button type="button" onClick={() => setisIp(true)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${isIp ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>True</button>
                    <button type="button" onClick={() => setisIp(false)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${!isIp ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>False</button>
                  </div>
                  <Label htmlFor="max_coverage_ip" className="text-xs font-semibold text-slate-600">Max Coverage IP</Label>
                  <Input id="max_coverage_ip" type="number" value={maxCoverageIp} onChange={(e) => setMaxCoverageIp(Number(e.target.value))} placeholder="0" className="mt-1.5 bg-white" />
                </FieldPanel>

                <FieldPanel title="Is OP">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button type="button" onClick={() => setisOp(true)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${isOp ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>True</button>
                    <button type="button" onClick={() => setisOp(false)} className={`py-2 rounded-lg text-xs font-bold border transition-all ${!isOp ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"}`}>False</button>
                  </div>
                  <Label htmlFor="max_coverage_op" className="text-xs font-semibold text-slate-600">Max Coverage OP</Label>
                  <Input id="max_coverage_op" type="number" value={maxCoverageOp} onChange={(e) => setMaxCoverageOp(Number(e.target.value))} placeholder="0" className="mt-1.5 bg-white" />
                </FieldPanel>

                <div>
                  <Label htmlFor="copay" className="text-xs font-semibold text-slate-600">Copay</Label>
                  <Input id="copay" type="number" value={copay} onChange={(e) => setCopay(Number(e.target.value))} placeholder="0" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label htmlFor="max_copay" className="text-xs font-semibold text-slate-600">Max Copay</Label>
                  <Input id="max_copay" type="number" value={maxCopay} onChange={(e) => setMaxCopay(Number(e.target.value))} placeholder="0" className="mt-1.5 bg-white" />
                </div>
              </div>
            </SectionCard>

            <SectionCard num={3} title="Drug">
              <div className="space-y-3 max-w-[520px]">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Drug Source</Label>
                  <Select value={selectedMainOption || undefined} onValueChange={(value) => setSelectedMainOption(value)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_Drug_code">List of Drug Code</SelectItem>
                      <SelectItem value="medical_Drug_groups">Medical Drug Groups</SelectItem>
                      <SelectItem value="custom_Drug_groups">Custom Drug Groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedMainOption === "list_of_Drug_code" && <div className="rounded-lg border border-orange-200 bg-orange-50 p-4"><ListOfDrugCode onChange={handleMainDrugChange} defaultValue={mainDrug} clear={clearMainDrug} /></div>}
                {selectedMainOption === "medical_Drug_groups" && <div className="rounded-lg border border-gray-100 bg-gray-50 p-4"><ListOfDrugMedical onChange={handleMainDrugMedicalGroupsChange} defaultValue={mainDrugMedicalGroups} clear={clearMainDrugMedicalGroups} /></div>}
                {selectedMainOption === "custom_Drug_groups" && <div className="rounded-lg border border-gray-100 bg-gray-50 p-4"><ListOfDrugCustom onChange={handleMainDrugCustomGroupsChange} defaultValue={mainDrugCustomGroups} clear={clearMainDrugCustomGroups} /></div>}
              </div>
            </SectionCard>

            <SectionCard num={4} title="Activity">
              {activityError && <ErrorBanner text={activityError} />}
              <div className="space-y-3 max-w-[520px]">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Activity Source <span className="text-red-500">*</span></Label>
                  <Select value={selectedActivityOption || undefined} onValueChange={(value) => setSelectedActivityOption(value)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_Activity_code">List of Activity Code</SelectItem>
                      <SelectItem value="medical_Activity_groups">Medical Activity Groups</SelectItem>
                      <SelectItem value="custom_Activity_groups">Custom Activity Groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedActivityOption === "list_of_Activity_code" && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                    <div>
                      <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Activity Type</Label>
                      <ActivitySelector onSelectActivity={handleActivityTypeChange} selectedActivityType={ActivityType} />
                    </div>
                    {ActivityType && <ListofActivityCode onChange={handleRelatedActivityChange} type={ActivityType} defaultValue={relatedActivity} clear={clearActivityCode} isValid={activityTypeisValid} />}
                  </div>
                )}
                {selectedActivityOption === "medical_Activity_groups" && <div className="rounded-lg border border-gray-100 bg-gray-50 p-4"><ListOfActivityMedical onChange={handleRelatedActivitiesMedicalGroupsChange} defaultValue={relatedActivitiesMedicalGroups} clear={clearRelatedActivitiesMedicalGroups} /></div>}
                {selectedActivityOption === "custom_Activity_groups" && <div className="rounded-lg border border-gray-100 bg-gray-50 p-4"><ListOfActivityCustom onChange={handleRelatedActivitiesCustomGroupsChange} defaultValue={relatedActivitiesCustomGroups} clear={clearRelatedActivitiesCustomGroups} /></div>}
              </div>
            </SectionCard>

            <SectionCard num={5} title="Primary Diagnosis">
              {icdError && <ErrorBanner text={icdError} />}
              <div className="space-y-3 max-w-[520px]">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Primary Diagnosis Source</Label>
                  <Select value={selectedPrimaryIcdOption || undefined} onValueChange={(value) => setSelectedPrimaryIcdOption(value)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_primary_icd">List of ICD</SelectItem>
                      <SelectItem value="medical_primary_icd_groups">Medical ICD Groups</SelectItem>
                      <SelectItem value="custom_primary_icd_groups">Custom ICD Groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedPrimaryIcdOption === "list_of_primary_icd" && <div className="mt-3"><Listofmedicalcode onChange={handlePrimaryDiagnosisChange} description={IcdDescription} defaultValue={PrimaryDiagnosis} clear={clearPrimaryDiagnosis} /></div>}
                {selectedPrimaryIcdOption === "medical_primary_icd_groups" && <div className="mt-3"><ListofMedicalGroup onChange={handleRelatedPrimaryMedicalGroupsChange} defaultValue={relatedPrimaryMedicalGroups} clear={clearRelatedPrimaryMedicalGroups} /></div>}
                {selectedPrimaryIcdOption === "custom_primary_icd_groups" && <div className="mt-3"><ListofCustomIcd onChange={handleRelatedPrimaryCustomGroupsChange} defaultValue={relatedPrimaryCustomGroups} clear={clearRelatedPrimaryCustomGroups} /></div>}
              </div>
            </SectionCard>

            <SectionCard num={6} title="Secondary Diagnosis">
              {icdError && <ErrorBanner text={icdError} />}
              <div className="space-y-3 max-w-[520px]">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Secondary Diagnosis Source</Label>
                  <Select value={selectedIcdOption || undefined} onValueChange={(value) => setSelectedIcdOption(value)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_icd">List of ICD</SelectItem>
                      <SelectItem value="medical_icd_groups">Medical ICD Groups</SelectItem>
                      <SelectItem value="custom_icd_groups">Custom ICD Groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedIcdOption === "list_of_icd" && <div className="mt-3"><Listofmedicalcode onChange={handleRelatedIcdsChange} description={IcdDescription} defaultValue={relatedIcds} clear={clearIcdmedicalcode} /></div>}
                {selectedIcdOption === "medical_icd_groups" && <div className="mt-3"><ListofMedicalGroup onChange={handleRelatedIcdMedicalGroupsChange} defaultValue={relatedIcdMedicalGroups} clear={clearRelatedIcdMedicalGroups} /></div>}
                {selectedIcdOption === "custom_icd_groups" && <div className="mt-3"><ListofCustomIcd onChange={handleRelatedIcdCustomGroupsChange} defaultValue={relatedIcdCustomGroups} clear={clearRelatedIcdCustomGroups} /></div>}
              </div>
            </SectionCard>

            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm bg-white">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">7</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Target Scope & Visibility</p>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-blue-50">
                <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                  <i className="fa-solid fa-circle-info text-blue-700 text-sm mt-0.5"></i>
                  <p className="text-xs text-blue-900 leading-relaxed m-0">
                    <span className="font-semibold text-red-700">Exclude</span> = apply to everyone except the selected items &nbsp;·&nbsp;
                    <span className="font-semibold text-emerald-700">Include</span> = apply only to the selected items
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <ScopeCard label="Providers" icon="fa-hospital" color="blue" isExclude={applyToOtherProviders} onModeChange={setApplyToOtherProviders}>
                    <ListOfProviderName onChange={handleProviderChange} defaultValue={provider} clear={clearProvider} />
                  </ScopeCard>

                  <ScopeCard label="Doctor" icon="fa-stethoscope" color="cyan" isExclude={applyToOtherDoctor} onModeChange={setApplyToOtherDoctor}>
                    <ListOfDoctorName onChange={handleDoctorChange} defaultValue={doctor} clear={clearDoctor} />
                  </ScopeCard>

                  <ScopeCard label="Networks" icon="fa-network-wired" color="purple" isExclude={apply_to_other_networks} onModeChange={setApply_to_other_networks}>
                    <ListOfNewNetworkName onChange={handleNetworkChange} defaultValue={networks} clear={clearNetworks} />
                  </ScopeCard>

                  <ScopeCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isExclude={apply_to_other_insuranceplan} onModeChange={setapply_to_other_insuranceplan}>
                    <ListOfInsuranceplanName onChange={handleInsuranceplanChange} defaultValue={insuranceplan} clear={clearInsuranceplan} />
                  </ScopeCard>

                  <ScopeCard label="Policy" icon="fa-file-lines" color="emerald" isExclude={apply_to_other_policy} onModeChange={setApply_to_other_policy}>
                    <PolicyNames onChange={handlePoliciesChange} defaultValue={policy} clear={clearPolicy} />
                  </ScopeCard>

                  <ScopeCard label="Patients" icon="fa-users" color="pink" isExclude={apply_to_other_patients} onModeChange={setApply_to_other_patients}>
                    <Patient onChange={handlePatientChange} defaultValue={patients} clear={clearPatient} />
                  </ScopeCard>

                  <ScopeCard label="VIP" icon="fa-star" color="yellow" isExclude={apply_to_other_vip} onModeChange={setApply_to_other_vip}>
                    <Vip onChange={handleVipChange} defaultValue={vip} clear={clearVip} />
                  </ScopeCard>

                  <ScopeCard label="Payer" icon="fa-building-columns" color="indigo" isExclude={apply_to_other_payer} onModeChange={setapply_to_other_payer}>
                    <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" className="bg-white" />
                  </ScopeCard>

                  <div className="lg:col-span-2">
                    <ScopeCard label="User Level" icon="fa-user-gear" color="slate" isExclude={apply_to_other_user} onModeChange={setApply_to_other_user}>
                      <Input value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter User Level" className="bg-white" />
                    </ScopeCard>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Benefit" : "Update Relation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
  <DialogContent className="max-w-3xl max-h-[80vh]">
    <DialogHeader>
      <DialogTitle>{allCodesTitle} ({allCodesList.length})</DialogTitle>
    </DialogHeader>
    <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
      {["icd","medical","custom"].map((kind) => {
        const filtered = allCodesList.filter(x => x.kind === kind);
        if (!filtered.length) return null;
        const s = kind === "icd"
          ? { bg: "#011934", color: "#fff", border: "#011934", label: "ICD Codes" }
          : kind === "medical"
          ? { bg: "#f0fdf4", color: "#15803d", border: "#86efac", label: "Medical Groups" }
          : { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", label: "Custom Groups" };
        return (
          <div key={kind}>
            <span className="text-xs font-bold text-gray-600 mb-2 block">{s.label} ({filtered.length})</span>
            <div className="flex flex-wrap gap-2">
              {filtered.map((c, i) => (
                <span key={i} style={{ padding: "4px 12px", background: s.bg, color: s.color, borderRadius: 6, fontSize: 12, fontWeight: 600, border: `1px solid ${s.border}` }}>{c.label}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
    <div className="flex justify-end pt-3 border-t mt-3">
      <Button onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
    </div>
  </DialogContent>
</Dialog>

      <Toaster />
    </div>
  );
};

export default Benefit;